import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { bulkPublishReplies, clearBulkDrafts, clearError, googleReviewErrorMessage } from '../store/slices/googleReviewSlice';
import { usePermissions } from '../hooks/usePermissions';
import { addNotification } from '../store/slices/notificationSlice';
import BulkReviewCard from '../components/google-review/BulkReviewCard';
import type { GoogleReviewView } from '../types/googleReview';
import BackButton from '../components/BackButton';

const GoogleReviewBulkReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Subuser: publishing replies requires google_review_write.
  const { can } = usePermissions();
  const canWrite = can('google_review_write');
  const { reviews, bulkDrafts, selectedReviewIds, isPublishing, error, errorCode } = useAppSelector(
    (s) => s.googleReview,
  );

  useEffect(() => {
    if (error) {
      dispatch(addNotification({ type: 'error', message: googleReviewErrorMessage(errorCode, error, t) }));
      dispatch(clearError());
    }
  }, [error, errorCode, dispatch, t]);

  const bulkReviews = useMemo(
    () => reviews.filter(
      (review): review is GoogleReviewView & { reviewId: string } =>
        typeof review.reviewId === 'string' && selectedReviewIds.includes(review.reviewId),
    ),
    [reviews, selectedReviewIds],
  );

  const readyCount = useMemo(
    () => bulkReviews.filter((r) => (bulkDrafts[r.reviewId] ?? '').length > 0).length,
    [bulkReviews, bulkDrafts],
  );

  const sentToDraftCount = useMemo(
    () => bulkReviews.filter((r) => r.status === 'draft').length,
    [bulkReviews],
  );

  const handlePublishAll = async () => {
    const items = bulkReviews
      .filter((r) => (bulkDrafts[r.reviewId] ?? '').length > 0)
      .map((r) => ({ reviewId: r.reviewId, text: bulkDrafts[r.reviewId] }));

    if (items.length === 0) return;
    await dispatch(bulkPublishReplies(items));
    dispatch(clearBulkDrafts());
    navigate('/features/google-review-manager');
  };

  const handleCancel = () => {
    dispatch(clearBulkDrafts());
    navigate('/features/google-review-manager');
  };

  return (
    <>
      <div className="google-review-bulk-page">
        <div className="google-review-bulk-page__banner" role="note">
          <span className="google-review-bulk-page__banner-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" stroke="white" strokeWidth="1.5" />
              <path d="M10 9v5M10 7h.01" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
          </span>
          <div>
            <strong>{t('google_review_bulk_banner_title')}</strong>
            <p>{t('google_review_bulk_banner_description')}</p>
          </div>
        </div>
        {/* <BackButton /> */}

        {/* Padded content */}
        <div className="google-review-bulk-page__content">
          {bulkReviews.length === 0 ? (
            <p className="google-review-bulk-page__empty">{t('google_review_bulk_empty')}</p>
          ) : (
            <div className="google-review-bulk-page__list">
              {bulkReviews.map((review) => (
                <BulkReviewCard
                  key={review.reviewId}
                  review={review}
                  draftText={bulkDrafts[review.reviewId] ?? ''}
                />
              ))}
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className="google-review-bulk-page__footer">
          <div className="google-review-bulk-page__footer-counts">
            <span>{t('google_review_bulk_footer_ready', { count: readyCount })}</span>
            {sentToDraftCount > 0 && (
              <span className="google-review-bulk-page__footer-draft">
                {t('google_review_bulk_footer_draft', { count: sentToDraftCount })}
              </span>
            )}
          </div>
          <div className="google-review-bulk-page__footer-actions">
            <button
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={isPublishing}
            >
              {t('google_review_bulk_cancel')}
            </button>
            <button
              className="btn btn-primary google-review-bulk-page__publish-btn"
              onClick={handlePublishAll}
              disabled={readyCount === 0 || isPublishing || !canWrite}
              title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
            >
              {isPublishing ? (
                <>
                  <span className="bulk-actions-bar__spinner bulk-actions-bar__spinner--white" aria-hidden="true" />
                  {t('google_review_bulk_publishing')}
                </>
              ) : (
                t('google_review_bulk_publish_all', { count: readyCount })
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GoogleReviewBulkReviewPage;
